import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { Topic } from 'models/topic';
import { SimpleTopic } from '../../../models/simpleTopic';
import { TopicAttachmentsVMConfig } from '../attachments/attachments';
import { TopicBoxVMConfig } from '../box/box';
import { TopicContentVMConfig } from '../content/content';
import './summary.html';
import './summary.scss';

components.preload('topic-box', 'topic-content', 'topic-content-image', 'topic-attachments', 'topic-summary-instructions-header');

export type TopicSummaryAttachmentsMode = 'auto' | 'alwaysActive' | 'hidden';

export interface TopicSummaryVMConfig {
    topicId?: SubscribableOrNullableValue<string>
    topicBox?: SubscribableOrNullableValue<boolean>
    spinner?: SubscribableOrNullableValue<boolean>
    attachments?: SubscribableOrNullableValue<TopicSummaryAttachmentsMode>

    content?: Omit<TopicContentVMConfig, 'topicId'>
}

@injectable()
export default class TopicSummaryVM {
    topicId: Subscribable<string | undefined>
    topic: Subscribable<Topic | undefined>

    hasAttachments: Subscribable<boolean>
    attachmentsAreAlwaysActive: Subscribable<boolean>

    isTopicBoxNeeded: Subscribable<boolean>
    isSpinnerNeeded: Subscribable<boolean>

    isReady: Subscribable<boolean>
    isLoading: Subscribable<boolean>
    areAttachmentsAllowed: Subscribable<boolean>

    isMaterialUIEnabled: PureComputed<boolean>

    topicConfig: TopicBoxVMConfig
    topicContentConfig: TopicContentVMConfig
    attachmentsConfig: TopicAttachmentsVMConfig

    constructor(
        @inject(wccModules.componentConfig) config: TopicSummaryVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.topicId = ko.flattenComputed(config.topicId);

        this.isTopicBoxNeeded = ko.flattenComputed(config.topicBox, true);
        this.isSpinnerNeeded = ko.flattenComputed(config.spinner, true);

        const attachmentsMode = ko.flattenComputed(config.attachments, 'auto');

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        const isLoadingTopic = topicManager.pluck(m => m.loading, true).extend({ deferred: true });

        this.topic = topicManager.pluck(m => m.topic);
        this.hasAttachments = this.topic.pluck(t => t.hasAttachments, false);
        this.attachmentsAreAlwaysActive = attachmentsMode.is('alwaysActive');

        this.isReady = ko.pureComputed(() => !isLoadingTopic());
        this.isLoading = this.isReady.not();
        this.areAttachmentsAllowed = ko.pureComputed(() => this.hasAttachments() && attachmentsMode() != 'hidden');

        this.isMaterialUIEnabled = ko.pureComputed(() => WCC.Settings.MaterialUIEnabled ?? false);

        this.topicConfig = {
            topic: <SubscribableOrNullableValue<Topic | SimpleTopic>>this.topic,
            theme: 'small-rounded',
            hideNotifications: true
        }

        this.topicContentConfig = {
            ...config.content,

            topicId: this.topicId,

            blockTheme: 'rounded'
        }

        this.attachmentsConfig = {
            topicId: this.topicId,
            itemWidth: 120,
            offset: 5,
            alwaysActive: this.attachmentsAreAlwaysActive,
            attachmentsTheme: 'light'            
        }

        ko.applyBindingsToNode(info.element, {
            'css': {
                'topic__summary': true
            }
        });
    }
}